import { lookupSectorMultiple } from "../data/sector-multiples";
import {
  getGeoAdjustment,
  blendMultiples,
  computeConfidence,
} from "./market-context";
import {
  REVENUE_CROSS_CHECK_PULL,
  type MultiplesValuationInput,
  type MultiplesValuationResult,
  type BlendingDetails,
} from "./types";
export * from "./types";
import {
  passiveManagementAdjustment,
  reputationAdjustment,
  cashflowVolatilityAdjustment,
  competitiveAdvantageAdjustment,
  sizeAdjustment,
  revenueTrendAdjustment,
} from "./adjustments";
import { validateMultiplesInput } from "../validation";

export function calculateMultiplesValuation(
  input: MultiplesValuationInput,
): MultiplesValuationResult {
  if (!input.skipValidation) {
    validateMultiplesInput({
      revenueLastTaxReturn: input.revenueLastTaxReturn,
      profitPlusOwnerCompensation: input.profitPlusOwnerCompensation,
      ownerHoursPerWeek: input.ownerHoursPerWeek,
      yearsOperating: input.yearsOperating,
      revenueHistory: input.revenueHistory,
    });
  }

  const domestic = !input.country || input.country === "us";
  if (!domestic && !input.marketOverrides)
    throw new Error("Country-specific market evidence is required");
  const sector = domestic
    ? lookupSectorMultiple(input.industry)
    : {
        revenueMultiple: input.marketOverrides?.revenueMultiple ?? 0,
        cashflowMultiple: input.marketOverrides?.sdeMultiple ?? 0,
        reportedSales: input.marketOverrides?.transactionCount ?? 0,
      };
  if (!sector) {
    throw new Error(`Unknown industry: "${input.industry}"`);
  }

  let baseRevenueMultiple = sector.revenueMultiple;
  let baseCashflowMultiple = sector.cashflowMultiple;
  let blending: BlendingDetails | null = null;

  if (input.marketOverrides && domestic) {
    const blended = blendMultiples(
      sector.revenueMultiple,
      sector.cashflowMultiple,
      sector.reportedSales,
      input.marketOverrides,
    );
    baseRevenueMultiple = blended.revMult;
    baseCashflowMultiple = blended.cfMult;
    blending = blended.blending;
  }

  const geo = getGeoAdjustment(domestic ? input.zipCode : undefined);
  const geoRevenueFactor = 1 + geo.revenueAdj;
  const geoCashflowFactor = 1 + geo.cashflowAdj;

  const volAdj = cashflowVolatilityAdjustment(input.volatileCashFlow);
  const passiveAdj = passiveManagementAdjustment(input.ownerHoursPerWeek);
  const repAdj = reputationAdjustment(input.yearsOperating);
  const advAdj = competitiveAdvantageAdjustment(input.sustainableAdvantage);
  const sizeAdj = domestic ? sizeAdjustment(input.revenueLastTaxReturn) : 0;

  const revenueHistory = input.revenueHistory ?? [input.revenueLastTaxReturn];
  const trendAdj = revenueTrendAdjustment(revenueHistory);

  const volatilityFactor = 1 + volAdj;
  const passiveFactor = 1 + passiveAdj;
  const reputationFactor = 1 + repAdj;
  const advantageFactor = 1 + advAdj;
  const sizeFactor = 1 + sizeAdj;
  const trendFactor = 1 + trendAdj;

  const adjustedRevenueMultiple =
    baseRevenueMultiple *
    geoRevenueFactor *
    volatilityFactor *
    passiveFactor *
    reputationFactor *
    advantageFactor *
    sizeFactor *
    trendFactor;

  const adjustedCashflowMultiple =
    baseCashflowMultiple *
    geoCashflowFactor *
    volatilityFactor *
    passiveFactor *
    reputationFactor *
    advantageFactor *
    sizeFactor *
    trendFactor;

  const revenueImpliedPrice =
    adjustedRevenueMultiple * input.revenueLastTaxReturn;
  const cashflowImpliedPrice =
    adjustedCashflowMultiple * input.profitPlusOwnerCompensation;

  // Earnings-led range: with verified SDE the cash-flow price anchors the
  // range and the revenue price only pulls it part-way. Without verified SDE
  // (industry-margin estimate) the two prices are near-collinear anyway, so
  // the legacy min/max blend stands.
  const rangeBasis: "earnings_led" | "blended" = input.sdeVerified
    ? "earnings_led"
    : "blended";
  const revenueCrossCheckPrice =
    rangeBasis === "earnings_led"
      ? cashflowImpliedPrice +
        (revenueImpliedPrice - cashflowImpliedPrice) * REVENUE_CROSS_CHECK_PULL
      : revenueImpliedPrice;
  const sectorImpliedSdeMargin =
    baseCashflowMultiple > 0
      ? baseRevenueMultiple / baseCashflowMultiple
      : null;
  const actualSdeMargin =
    input.revenueLastTaxReturn > 0
      ? input.profitPlusOwnerCompensation / input.revenueLastTaxReturn
      : null;

  const valuationMin = Math.min(revenueCrossCheckPrice, cashflowImpliedPrice);
  const valuationMax = Math.max(revenueCrossCheckPrice, cashflowImpliedPrice);

  const confidence = computeConfidence(
    sector.reportedSales,
    geo.geoLevel,
    revenueImpliedPrice,
    cashflowImpliedPrice,
    revenueHistory.length,
    !!input.marketOverrides,
  );

  return {
    valuationMin,
    valuationMax,
    details: {
      baseRevenueMultiple,
      baseCashflowMultiple,
      geoRevenueAdjustment: geo.revenueAdj,
      geoCashflowAdjustment: geo.cashflowAdj,
      cashflowVolatilityAdjustment: volAdj,
      passiveManagementAdjustment: passiveAdj,
      reputationAdjustment: repAdj,
      competitiveAdvantageAdjustment: advAdj,
      sizeAdjustment: sizeAdj,
      revenueTrendAdjustment: trendAdj,
      adjustedRevenueMultiple,
      adjustedCashflowMultiple,
      revenueImpliedPrice,
      cashflowImpliedPrice,
      rangeBasis,
      sectorImpliedSdeMargin,
      actualSdeMargin,
      revenueCrossCheckPrice,
      cbsa: geo.cbsa,
      state: geo.state,
      blending,
    },
    confidence,
  };
}
